import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FolderIcon from '@mui/icons-material/Folder';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import fileSystemAccessServiceInstance from '../../services/FileSystemAccessService';
import { formatPath } from '../../utils/formatUtils';

const MoveDialog = ({ open, onClose, onMove, fileName, currentPath, isFolder = false }) => {
  const [loading, setLoading] = useState(false);
  const [folders, setFolders] = useState([]);
  const [currentBrowsePath, setCurrentBrowsePath] = useState('/');
  const [selectedPath, setSelectedPath] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([{ name: 'Root', path: '/' }]);
  const [error, setError] = useState(null);

  const loadFolders = async (path) => {
    setError(null);
    setLoading(true);
    
    try {
      const userData = JSON.parse(localStorage.getItem('authUser'));
      if (!userData) {
        setError('User data not found');
        setLoading(false);
        return;
      }

      const response = await fileSystemAccessServiceInstance.getFolderContents(
        path, 
        userData.email, 
        userData.access
      );

      // Filter out the current item if we're moving a folder
      const folderItems = (response.data || [])
        .filter(item => item && item.isFolder === true)
        // Don't show the folder we're currently moving
        .filter(folder => {
          if (!isFolder) return true; // If moving a video, show all folders
          
          // If moving a folder, don't show itself or any subfolders (to prevent moving into own subfolder)
          const folderPath = folder.path || `${path === '/' ? '' : path}/${folder.name}`;
          return fileName !== folder.name || formattedSourcePath !== folderPath;
        })
        .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
      
      setFolders(folderItems);
      updateBreadcrumbs(path);
      
    } catch (err) {
      console.error('Error loading folders:', err);
      setError('Failed to load folders. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const updateBreadcrumbs = (path) => {
    const parts = path.split('/').filter(Boolean);
    const crumbs = [{ name: 'Root', path: '/' }];
    
    let currentPath = '';
    for (const part of parts) {
      currentPath += '/' + part;
      crumbs.push({
        name: part,
        path: currentPath
      });
    }
    
    setBreadcrumbs(crumbs);
  };

  useEffect(() => {
    if (open) {
      setCurrentBrowsePath('/');
      setSelectedPath(null);
      loadFolders('/');
    }
  }, [open]);

  const handleFolderClick = (folder) => {
    const folderPath = folder.path;
    setCurrentBrowsePath(folderPath);
    loadFolders(folderPath);
  };

  const handleBreadcrumbClick = (path) => {
    setCurrentBrowsePath(path);
    loadFolders(path);
  };

  const handleGoBack = () => {
    if (currentBrowsePath === '/') return;
    
    const pathParts = currentBrowsePath.split('/').filter(Boolean);
    pathParts.pop();
    const parentPath = pathParts.length ? '/' + pathParts.join('/') : '/';
    
    setCurrentBrowsePath(parentPath);
    loadFolders(parentPath);
  };

  const handleSelectCurrentFolder = () => {
    setSelectedPath(currentBrowsePath);
  };

  const handleMove = () => {
    // Check if the current path is valid for moving
    if (currentBrowsePath !== formattedSourcePath) {
      onMove(currentBrowsePath);
      onClose();
    }
  };

  // Is the currently browsed path different from the original source path
  const canSelectCurrentPath = currentBrowsePath !== currentPath && currentBrowsePath !== '/' + currentPath;
  
  // Is the current path the root
  const isRootPath = currentBrowsePath === '/';
  
  

  // Simplified source path for display (removing multiple slashes)
  const formattedSourcePath = currentPath.replace(/\/+/g, '/');
  
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh',
        }
      }}
    >
      <DialogTitle sx={{ pb: 0, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6">Move {isFolder ? 'Folder' : 'Video'}</Typography>
      </DialogTitle>
      
      {/* Source and destination summary */}
      <Box sx={{ px: 3, py: 1.5, backgroundColor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Source */}
            <Box sx={{ display: 'flex', alignItems: 'center', mr: 2, my: 1 }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                p: 1, 
                backgroundColor: 'background.default',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                minWidth: '180px'
              }}>
                {isFolder ? (
                  <FolderIcon color="primary" sx={{ mr: 1 }} />
                ) : (
                  <VideoFileIcon color="secondary" sx={{ mr: 1 }} />
                )}
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Moving
                  </Typography>
                  <Typography variant="body2" fontWeight="medium" sx={{ wordBreak: 'break-word' }}>
                    {fileName || 'Selected item'}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Arrow */}
            <ArrowForwardIcon color="action" sx={{ mx: 1, my: 1 }} />
            
            {/* Destination */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              p: 1,
              backgroundColor: 'action.hover',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              my: 1,
              flexGrow: 1
            }}>
              <FolderIcon color="primary" sx={{ mr: 1 }} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Destination folder
                </Typography>
                <Typography variant="body2" fontWeight="medium" sx={{ wordBreak: 'break-word' }}>
                  {currentBrowsePath === '/' ? 'Home' : formatPath(currentBrowsePath)}
                </Typography>
              </Box>
            </Box>
          </Box>
          
          {/* Source path information */}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Will be moved from: {formattedSourcePath === '/' ? 'Home' : formatPath(formattedSourcePath)}
          </Typography>
        </Paper>
      </Box>
      
      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ 
          backgroundColor: 'background.default', 
          borderBottom: '1px solid', 
          borderColor: 'divider',
          p: 1.5
        }}>
          {/* Breadcrumb navigation */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
            {breadcrumbs.map((crumb, index) => (
              <React.Fragment key={crumb.path}>
                <Chip
                  icon={index === 0 ? <HomeIcon fontSize="small" /> : null}
                  label={index === 0 ? "" : crumb.name}  // Don't show "Root" text for home
                  size="small"
                  clickable={crumb.path !== currentBrowsePath}
                  onClick={() => handleBreadcrumbClick(crumb.path)}
                  variant={crumb.path === currentBrowsePath ? "filled" : "outlined"}
                  color={crumb.path === currentBrowsePath ? "primary" : "default"}
                  sx={index === 0 ? { 
                    '& .MuiChip-label': { 
                      padding: '0px'  // Remove padding for empty label
                    },
                    '& .MuiChip-icon': {
                      margin: '0 auto',  // Center the icon horizontally
                      marginLeft: '8px',  // Add appropriate left margin
                      marginRight: '8px'  // Add appropriate right margin
                    },
                    height: '24px',       // Ensure consistent height
                    width: '32px'         // Fixed width for home icon chip
                  } : {}}
                />
                {index < breadcrumbs.length - 1 && (
                  <NavigateNextIcon fontSize="small" color="disabled" />
                )}
              </React.Fragment>
            ))}
          </Box>
        </Box>
        
        {error && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography color="error">{error}</Typography>
            <Button 
              variant="outlined" 
              onClick={() => loadFolders(currentBrowsePath)} 
              sx={{ mt: 1 }}
            >
              Try Again
            </Button>
          </Box>
        )}
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ py: 0 }}>
            {/* Back button for non-root folders */}
            {!isRootPath && (
              <ListItemButton onClick={handleGoBack} divider>
                <ListItemIcon>
                  <ArrowBackIcon color="action" />
                </ListItemIcon>
                <ListItemText 
                  primary="Go back to parent folder" 
                  //secondary={breadcrumbs[breadcrumbs.length - 2]?.path || '/'}
                />
              </ListItemButton>
            )}
            
            {/* Folder list */}
            {folders.length === 0 ? (
              <Box sx={{ 
                p: 4, 
                textAlign: 'center',
                borderRadius: 1
              }}>
                {currentBrowsePath === formattedSourcePath ? (
                  // Can't move to same location
                  <Typography color="warning.main">
                    You can't move an item to its current location
                  </Typography>
                ) : (
                  <Typography color="text.secondary">
                    No sub-folders found in this location
                  </Typography>
                )}
              </Box>
            ) : (
              <List>
                {folders.map((folder) => (
                  <ListItemButton 
                    key={folder.path || folder.name} 
                    onClick={() => handleFolderClick(folder)}
                    divider
                    sx={{ 
                      position: 'relative'
                    }}
                  >
                    <ListItemIcon>
                      <FolderIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={folder.name} 
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 2, display: 'flex', justifyContent: 'space-between' }}>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        
        <Button 
          onClick={handleMove} 
          variant="contained"
          color="primary"
          disabled={loading || currentBrowsePath === formattedSourcePath}
        >
          Move
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MoveDialog;